import { ActionType } from '../../../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../../../core/api/rsaa';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { getLoading, addError, noLoadingNoError, addLoading } from '../../../../core/api/module';


const KEY_ACTION = ActionType.SendParticipantCommentNotificationRequest;
const ACTIONS    = [
  ActionType.SendParticipantCommentNotificationRequest,
  ActionType.SendParticipantCommentNotificationResponse,
  ActionType.SendParticipantCommentNotificationError,
];


type Payload = string;


type Meta = {
  projectId: number;
  devItemId: number;
  devActionId: number;
};


/**
 * Dispatch this to send an email notifying all dev plan collaborators of a new participant comment
 *
 * @param projectId - the database Id for the project that's been commented-on
 * @param participantId - the database Id for the participant receiving the comment
 * @param devItemId - the database Id for the commented dev item
 * @param devActionId - the database Id for the specific action in the dev item to which the comment is to be added
 * @param collaboratorEmails - the email addresses of the dev plan collaborators
 * @param comment - the text content of the newly added comment
 * @param languageId - the language in which notifications should be sent (usually just the project default language)
 */
export const sendParticipantCommentNotificationAction = (
  projectId: number,
  participantId: number,
  devItemId: number,
  devActionId: number,
  collaboratorEmails: string[],
  comment: string,
  languageId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.SendParticipantCommentNotification,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:         projectId,
        participantId:     participantId,
        comment:           comment,
        to:                collaboratorEmails.map(email => ({ toAddress: email, languageId: languageId })),
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId:   projectId,
        devItemId:   devItemId,
        devActionId: devActionId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${devItemId}-${devActionId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, devItemId, devActionId } = action.meta;

  const metaTag = `${projectId}-${devItemId}-${devActionId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  return noLoadingNoError(state, KEY_ACTION, metaTag);
};

export const sendParticipantCommentNotificationReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
